import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createVNode as _createVNode, createCommentVNode as _createCommentVNode, createBlock as _createBlock, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, vModelText as _vModelText, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "aura-chat-layout flex h-full min-h-0 flex-col bg-white" }
const _hoisted_2 = {
  key: 0,
  class: "flex h-full flex-col items-center justify-center px-2 sm:px-4"
}
const _hoisted_3 = ["src"]
const _hoisted_4 = { class: "mb-2 text-center text-xl font-bold text-[#242B59] sm:text-2xl" }
const _hoisted_5 = { class: "mb-8 text-center text-[#626468]" }
const _hoisted_6 = { class: "grid w-full max-w-3xl grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4" }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "text-sm font-medium text-[#626468]" }
const _hoisted_9 = ["src"]
const _hoisted_10 = {
  key: 1,
  class: "w-full space-y-4"
}
const _hoisted_11 = { class: "max-w-[90%] rounded-2xl rounded-bl-md bg-gray-50 px-4 py-2.5 text-[#242B59] sm:max-w-[85%]" }
const _hoisted_12 = {
  key: 0,
  class: "mt-2 pt-1"
}
const _hoisted_13 = ["onClick"]
const _hoisted_14 = {
  key: 1,
  class: "mt-3 space-y-2"
}
const _hoisted_15 = { class: "text-xs font-semibold uppercase tracking-wider text-[#737373]" }
const _hoisted_16 = { class: "flex flex-wrap gap-2" }
const _hoisted_17 = ["onClick"]
const _hoisted_18 = {
  key: 2,
  class: "grid grid-cols-2 gap-3"
}
const _hoisted_19 = { class: "text-xs text-[#808288]" }
const _hoisted_20 = { class: "text-lg font-bold text-[#242B59]" }
const _hoisted_21 = {
  key: 5,
  class: "flex flex-wrap gap-3"
}
const _hoisted_22 = {
  key: 6,
  class: "space-y-3"
}
const _hoisted_23 = {
  key: 0,
  class: "flex flex-row gap-3"
}
const _hoisted_24 = { class: "flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl bg-[#48DDC3]/20" }
const _hoisted_25 = ["src"]
const _hoisted_26 = { class: "rounded-2xl rounded-bl-md bg-gray-50 px-4 py-3" }
const _hoisted_27 = {
  key: 0,
  class: "aura-typewriter mb-2 text-xs font-medium text-[#626468]"
}
const _hoisted_28 = { class: "aura-chat-footer shrink-0 border-t border-gray-100 bg-white px-4 py-3 sm:px-6 sm:py-4" }
const _hoisted_29 = { class: "aura-chat-input mx-auto flex max-w-3xl items-end gap-3" }
const _hoisted_30 = { class: "aura-chat-input-field relative flex flex-1 items-center gap-3 rounded-full border border-[#E5E5E5] bg-white px-4 py-3 shadow-[0px_2px_12px_rgba(0,0,0,0.03)] transition-all focus-within:border-[#48DDC3] focus-within:shadow-md" }
const _hoisted_31 = ["disabled"]
const _hoisted_32 = ["disabled"]
const _hoisted_33 = { class: "mt-2 flex items-start justify-between gap-3 text-[11px] leading-4 text-gray-400 sm:mt-3 sm:items-center sm:text-xs" }
const _hoisted_34 = { class: "min-w-0" }
const _hoisted_35 = { class: "shrink-0 whitespace-nowrap" }

import { ref, computed, nextTick, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useTypewriter } from '@/composables/useTypewriter';
import { UserIcon, PaperAirplaneIcon, PlusIcon } from '@heroicons/vue/solid';
import type { AuraAction, AuraMessage } from '@/types';
import { formatAuraDateLabel, formatAuraMetric } from '@/utils/auraPresentation';
import AuraActionCard from './components/AuraActionCard.vue';
import AuraChartBlock from './components/AuraChartBlock.vue';
import AuraInsightCard from './components/AuraInsightCard.vue';
import AuraRichText from './components/AuraRichText.vue';
import AuraVisualizationPanel from './components/AuraVisualizationPanel.vue';

// AURA Icons
import auraMainIcon from '@/assets/icons/aura-main.svg';
import auraConversationIcon from '@/assets/icons/aura-conversation.svg';

interface Props {
    messages?: AuraMessage[];
    isLoading?: boolean;
    disabled?: boolean;
    emptyPrompts?: string[];
    contextTitle?: string;
    loadingMessage?: string;
}

const MAX_MESSAGE_LENGTH = 2000;


export default /*@__PURE__*/_defineComponent({
  __name: 'AuraChatLayout',
  props: {
    messages: { default: () => [] },
    isLoading: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    emptyPrompts: { default: () => [] },
    contextTitle: { default: '' },
    loadingMessage: { default: '' }
  },
  emits: ["send", "retry-failed-question", "send-with-filter"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

/**
 * AURA Chat Layout - Main chat interface layout
 * [FE] Implementar layout principal do chat
 */
const props = __props;
const { t, locale } = useI18n();

const emit = __emit;

const inputMessage = ref('');
const messagesContainer = ref<HTMLElement | null>(null);
const textareaRef = ref<HTMLTextAreaElement | null>(null);
const canSend = computed(() => {
    return inputMessage.value.trim().length > 0 && !props.isLoading && !props.disabled;
});

const messageLength = computed(() => inputMessage.value.length);
const remainingCharacters = computed(() => Math.max(MAX_MESSAGE_LENGTH - messageLength.value, 0));

const hasMessages = computed(() => props.messages.length > 0);

const adjustTextareaHeight = () => {
    if (textareaRef.value) {
        textareaRef.value.style.height = 'auto';
        textareaRef.value.style.height = `${Math.min(textareaRef.value.scrollHeight, 120)}px`;
    }
};

const retryFailedQuestion = (auraMsgIndex: number) => {
    const userMsgIndex = auraMsgIndex - 1;
    let questionText = '';
    if (userMsgIndex >= 0 && props.messages[userMsgIndex]?.type === 'user') {
        questionText = props.messages[userMsgIndex].content;
    }

    inputMessage.value = questionText;

    nextTick(() => {
        textareaRef.value?.focus();
        adjustTextareaHeight();
    });

    emit('retry-failed-question', {
        userMsgIndex,
        auraMsgIndex,
        question: questionText,
    });
};

const sendMessage = () => {
    if (!canSend.value) return;

    const message = inputMessage.value.trim();
    emit('send', message);
    inputMessage.value = '';

    // Reset textarea height
    if (textareaRef.value) {
        textareaRef.value.style.height = 'auto';
    }

    // Auto-scroll to bottom
    nextTick(() => {
        scrollToBottom();
    });
};

const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        sendMessage();
    }
};

const handleInput = (e: Event) => {
    const target = e.target as HTMLTextAreaElement;
    target.style.height = 'auto';
    target.style.height = `${Math.min(target.scrollHeight, 120)}px`;
};

const scrollToBottom = () => {
    if (messagesContainer.value) {
        messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
};

// Auto-scroll when messages change
watch(
    () => props.messages.length,
    () => {
        nextTick(() => {
            scrollToBottom();
        });
    },
);

const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString(locale.value, {
        hour: '2-digit',
        minute: '2-digit',
    });
};

const selectPrompt = (prompt: string) => {
    inputMessage.value = prompt;
    if (textareaRef.value) {
        textareaRef.value.focus();
    }
};

const focusComposer = () => {
    textareaRef.value?.focus();
};

const executeAction = (action: AuraAction) => {
    const followUpQuestion = action.followUpQuestion?.trim();
    if (!followUpQuestion || props.isLoading || props.disabled) return;
    emit('send', followUpQuestion);
};

const getFilterDisambiguationOptions = (message: AuraMessage) => {
    return (
        message.response?.status?.details?.options ||
        message.response?.status?.filter_conflict?.matches ||
        []
    );
};

const getFilterDisambiguationFilterKey = (message: AuraMessage) => {
    return message.response?.status?.filter_conflict?.filterKey || 'userId';
};

const getFilterDisambiguationLabel = (message: AuraMessage) => {
    const code = message.response?.status?.code;
    if (code === 'AGENT_NAME_AMBIGUOUS') {
        return t('aura.chat.selectAgent');
    }
    return t('aura.chat.selectFilterOption');
};

const getKpiUnit = (message: AuraMessage, unit?: string): string | undefined => {
    return (
        unit || message.response?.chart?.unit || message.response?.visualization?.charts[0]?.unit
    );
};

// Typewriter para loading message — anima quando isLoading=true e loadingMessage muda
const { displayed: typewriterText } = useTypewriter(() =>
    props.isLoading ? (props.loadingMessage ?? '') : '',
);

__expose({
    focusComposer,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      ref_key: "messagesContainer",
      ref: messagesContainer,
      class: "min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4 sm:px-6"
    }, [
      (!hasMessages.value && !_ctx.isLoading)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _createElementVNode("img", {
              src: _unref(auraMainIcon),
              class: "mb-6 h-16 w-16",
              alt: "AURA"
            }, null, 8, _hoisted_3),
            _createElementVNode("h2", _hoisted_4, _toDisplayString(_ctx.$t('aura.chat.emptyState.title')), 1),
            _createElementVNode("p", _hoisted_5, _toDisplayString(props.contextTitle
                            ? _ctx.$t('aura.chat.emptyState.contextSubtitle', {
                                  context: props.contextTitle,
                              })
                            : _ctx.$t('aura.chat.emptyState.fallbackSubtitle')), 1),
            _createElementVNode("div", _hoisted_6, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(props.emptyPrompts, (prompt, index) => {
                return (_openBlock(), _createElementBlock("button", {
                  key: index,
                  class: "flex items-center justify-between gap-3 rounded-2xl border border-[#E5E5E5] bg-white p-4 text-left shadow-[0px_1px_3px_rgba(0,0,0,0.1),0px_1px_2px_-1px_rgba(0,0,0,0.1)] transition-all hover:border-[#48DDC3] hover:shadow-md",
                  onClick: ($event: any) => (selectPrompt(prompt))
                }, [
                  _createElementVNode("span", _hoisted_8, _toDisplayString(prompt), 1),
                  _createVNode(_unref(PlusIcon), { class: "h-5 w-5 flex-shrink-0 text-[#A1A1A1]" })
                ], 8, _hoisted_7))
              }), 128))
            ])
          ]))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(props.messages, (message, index) => {
              return (_openBlock(), _createElementBlock("div", {
                key: message.id || index,
                class: _normalizeClass([
                        'flex min-w-0 gap-3',
                        message.type === 'user' ? 'flex-row-reverse' : 'flex-row',
                    ])
              }, [
                _createElementVNode("div", {
                  class: _normalizeClass([
                            'flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl',
                            message.type === 'user' ? 'bg-[#242B59] text-white' : 'bg-[#48DDC3]/20',
                        ])
                }, [
                  (message.type === 'user')
                    ? (_openBlock(), _createBlock(_unref(UserIcon), {
                        key: 0,
                        class: "h-5 w-5"
                      }))
                    : (_openBlock(), _createElementBlock("img", {
                        key: 1,
                        src: _unref(auraConversationIcon),
                        class: "h-full w-full",
                        alt: "AURA"
                      }, null, 8, _hoisted_9))
                ], 2),
                _createElementVNode("div", {
                  class: _normalizeClass(["aura-message-bubble", [
                            'min-w-0',
                            message.type === 'user'
                                ? 'max-w-[90%] rounded-2xl rounded-br-md bg-[#242B59] px-4 py-2.5 text-white sm:max-w-[85%]'
                                : message.response
                                  ? 'max-w-full flex-1'
                                  : message.type === 'error'
                                    ? 'max-w-[90%] rounded-2xl rounded-bl-md border border-red-200 bg-red-50 px-4 py-2.5 text-red-800 sm:max-w-[85%]'
                                    : 'max-w-[90%] rounded-2xl rounded-bl-md bg-gray-50 px-4 py-2.5 text-[#242B59] sm:max-w-[85%]',
                        ]])
                }, [
                  (!message.response)
                    ? (_openBlock(), _createBlock(AuraRichText, {
                        key: 0,
                        class: "whitespace-pre-wrap text-sm",
                        text: message.content
                      }, null, 8, ["text"]))
                    : (_openBlock(), _createElementBlock("div", _hoisted_10, [
                        _createElementVNode("div", _hoisted_11, [
                          _createVNode(AuraRichText, {
                            class: "whitespace-pre-wrap text-sm",
                            text: 
                                        message.response.summary?.content ||
                                        message.response.status?.message ||
                                        message.content
                                    
                          }, null, 8, ["text"])
                        ]),
                        (
                                    message.response?.status?.code === 'BLOCKED_TEMPORAL_CONFLICT'
                                )
                          ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                              _createElementVNode("button", {
                                type: "button",
                                class: "inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-[#A16207] shadow-sm transition-all hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1",
                                onClick: ($event: any) => (retryFailedQuestion(index))
                              }, _cache[1] || (_cache[1] = [
                                _createElementVNode("svg", {
                                  class: "h-3.5 w-3.5",
                                  fill: "none",
                                  viewBox: "0 0 24 24",
                                  stroke: "currentColor",
                                  "stroke-width": "2"
                                }, [
                                  _createElementVNode("path", {
                                    "stroke-linecap": "round",
                                    "stroke-linejoin": "round",
                                    d: "M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 7.89M9 11l3 3L22 4"
                                  })
                                ], -1),
                                _createTextVNode(" Perguntar novamente ", -1)
                              ]), 8, _hoisted_13)
                            ]))
                          : _createCommentVNode("", true),
                        (
                                    [
                                        'FILTER_DISAMBIGUATION',
                                        'DISAMBIGUATION_REQUIRED',
                                        'AGENT_NAME_AMBIGUOUS',
                                    ].includes(message.response?.status?.code)
                                )
                          ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                              _createElementVNode("p", _hoisted_15, _toDisplayString(getFilterDisambiguationLabel(message)), 1),
                              _createElementVNode("div", _hoisted_16, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(getFilterDisambiguationOptions(message), (match) => {
                                  return (_openBlock(), _createElementBlock("button", {
                                    key: match.id,
                                    type: "button",
                                    class: "rounded-lg border border-[#48DDC3] bg-[#48DDC3]/10 px-3 py-1.5 text-sm font-medium text-[#242B59] transition-all hover:bg-[#48DDC3]/30",
                                    onClick: ($event: any) => (
                                            _ctx.$emit('send-with-filter', {
                                                message: _ctx.messages[index - 1]?.content,
                                                filterKey:
                                                    getFilterDisambiguationFilterKey(message),
                                                filterId: Number(match.id),
                                            })
                                        )
                                  }, _toDisplayString(match.label || match.name), 9, _hoisted_17))
                                }), 128))
                              ])
                            ]))
                          : _createCommentVNode("", true),
                        (message.response.kpis?.items?.length)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(message.response.kpis.items, (kpi) => {
                                return (_openBlock(), _createElementBlock("div", {
                                  key: kpi.name,
                                  class: "rounded-xl border border-[#E5E5E5] bg-white p-3"
                                }, [
                                  _createElementVNode("p", _hoisted_19, _toDisplayString(_unref(formatAuraDateLabel)(kpi.name)), 1),
                                  _createElementVNode("p", _hoisted_20, _toDisplayString(_unref(formatAuraMetric)(
                                                kpi.value,
                                                getKpiUnit(message, kpi.unit),
                                            )), 1),
                                  (kpi.trendValue)
                                    ? (_openBlock(), _createElementBlock("p", {
                                        key: 0,
                                        class: _normalizeClass([
                                            'text-xs',
                                            kpi.trend === 'up'
                                                ? 'text-green-500'
                                                : kpi.trend === 'down'
                                                  ? 'text-red-500'
                                                  : 'text-gray-400',
                                        ])
                                      }, _toDisplayString(kpi.trendValue), 3))
                                    : _createCommentVNode("", true)
                                ]))
                              }), 128))
                            ]))
                          : _createCommentVNode("", true),
                        (message.response.visualization?.status === 'complete')
                          ? (_openBlock(), _createBlock(AuraVisualizationPanel, {
                              key: 3,
                              plan: message.response.visualization
                            }, null, 8, ["plan"]))
                          : (message.response.chart?.status === 'complete')
                            ? (_openBlock(), _createBlock(AuraChartBlock, {
                                key: 4,
                                chart: message.response.chart
                              }, null, 8, ["chart"]))
                            : _createCommentVNode("", true),
                        (message.response.insights?.items?.length)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(message.response.insights.items, (insight, idx) => {
                                return (_openBlock(), _createElementBlock("div", {
                                  key: idx,
                                  class: "w-full sm:w-auto"
                                }, [
                                  _createVNode(AuraInsightCard, { insight: insight }, null, 8, ["insight"])
                                ]))
                              }), 128))
                            ]))
                          : _createCommentVNode("", true),
                        (message.response.actions?.items?.length)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(message.response.actions.items, (action, idx) => {
                                return (_openBlock(), _createBlock(AuraActionCard, {
                                  key: idx,
                                  action: action,
                                  loading: _ctx.isLoading,
                                  disabled: _ctx.disabled,
                                  onExecute: executeAction
                                }, null, 8, ["action", "loading", "disabled"]))
                              }), 128))
                            ]))
                          : _createCommentVNode("", true)
                      ])),
                  _createElementVNode("span", {
                    class: _normalizeClass([
                                'mt-1 block text-xs',
                                message.type === 'user' ? 'text-white/70' : 'text-gray-400',
                            ])
                  }, _toDisplayString(formatTime(message.createdAt)), 3)
                ], 2)
              ], 2))
            }), 128)),
            (_ctx.isLoading)
              ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
                  _createElementVNode("div", _hoisted_24, [
                    _createElementVNode("img", {
                      src: _unref(auraConversationIcon),
                      class: "h-full w-full",
                      alt: "AURA"
                    }, null, 8, _hoisted_25)
                  ]),
                  _createElementVNode("div", _hoisted_26, [
                    (_ctx.isLoading && _ctx.loadingMessage)
                      ? (_openBlock(), _createElementBlock("p", _hoisted_27, _toDisplayString(_unref(typewriterText)), 1))
                      : _createCommentVNode("", true),
                    _cache[2] || (_cache[2] = _createElementVNode("div", { class: "flex items-center gap-2" }, [
                      _createElementVNode("div", { class: "h-2 w-2 animate-bounce rounded-full bg-[#48DDC3] [animation-delay:-0.3s]" }),
                      _createElementVNode("div", { class: "h-2 w-2 animate-bounce rounded-full bg-[#48DDC3] [animation-delay:-0.15s]" }),
                      _createElementVNode("div", { class: "h-2 w-2 animate-bounce rounded-full bg-[#48DDC3]" })
                    ], -1))
                  ])
                ]))
              : _createCommentVNode("", true)
          ], 64))
    ], 512),
    _createElementVNode("div", _hoisted_28, [
      _createElementVNode("div", _hoisted_29, [
        _createElementVNode("div", _hoisted_30, [
          _createVNode(_unref(PlusIcon), { class: "h-5 w-5 text-gray-400" }),
          _withDirectives(_createElementVNode("textarea", {
            ref_key: "textareaRef",
            ref: textareaRef,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((inputMessage).value = $event)),
            disabled: _ctx.disabled,
            maxlength: MAX_MESSAGE_LENGTH,
            rows: "1",
            class: "w-full resize-none border-0 bg-transparent text-sm text-[#242B59] placeholder-gray-400 focus:outline-none focus:ring-0",
            placeholder: "Pergunte alguma coisa",
            onKeydown: handleKeydown,
            onInput: handleInput
          }, null, 40, _hoisted_31), [
            [_vModelText, inputMessage.value]
          ])
        ]),
        _createElementVNode("button", {
          disabled: !canSend.value,
          class: "flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-[#F5F5F5] text-gray-400 transition-all hover:bg-[#242B59] hover:text-white disabled:cursor-not-allowed disabled:opacity-50",
          onClick: sendMessage
        }, [
          _createVNode(_unref(PaperAirplaneIcon), { class: "h-5 w-5" })
        ], 8, _hoisted_32)
      ]),
      _createElementVNode("div", _hoisted_33, [
        _createElementVNode("p", _hoisted_34, _toDisplayString(_ctx.$t('aura.chat.disclaimer')), 1),
        _createElementVNode("p", _hoisted_35, _toDisplayString(_ctx.$t('aura.chat.remainingCharacters', { count: remainingCharacters.value })), 1)
      ])
    ])
  ]))
}
}

})